import React from "react";
import type { BillingPlan } from "@common/utils/types";
import { formatCurrency, formatDate, formatIntervalBadge, formatTimeSaved } from "./billingUtils";
import cn from "classnames";
import { useCreatePortalSession } from "@/state/hooks/useBilling";

// ---------------------------------------------------------------------------
// BillingCardShell — the standard white card wrapper used by most billing cards
// ---------------------------------------------------------------------------

interface BillingCardShellProps {
    children: React.ReactNode;
    className?: string;
}

export function BillingCardShell({ children, className }: BillingCardShellProps): React.JSX.Element {
    return (
        <div className={cn("flex flex-col bg-white gap-4 border border-gray-200 rounded-md p-8", className)}>
            {children}
        </div>
    );
}

// ---------------------------------------------------------------------------
// IntervalBadge — the rounded pill showing "Annual" or "Monthly"
// ---------------------------------------------------------------------------

interface IntervalBadgeProps {
    interval: BillingPlan["billing_interval"];
    className?: string;
}

export function IntervalBadge({ interval, className }: IntervalBadgeProps): React.JSX.Element | null {
    const label = formatIntervalBadge(interval);
    if (!label) return null;

    return (
        <span className={cn("rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-600", className)}>
            {label}
        </span>
    );
}

// ---------------------------------------------------------------------------
// CardUnpaidAlert — inline unpaid invoice warning shown within billing cards
// (distinct from the top-of-page UnpaidInvoiceBanner in common/components)
// ---------------------------------------------------------------------------

interface CardUnpaidAlertProps {
    unpaidInvoice: BillingPlan["unpaid_invoice"];
}

export function CardUnpaidAlert({ unpaidInvoice }: CardUnpaidAlertProps): React.JSX.Element | null {
    // Hook must run before any early return (rules of hooks).
    const portalSession = useCreatePortalSession();
    if (!unpaidInvoice) return null;

    return (
        <div className="flex items-center justify-between rounded-lg bg-red-50 px-4 py-3">
            <p className="text-sm text-red-700">
                You have an outstanding balance of{" "}
                <span className="font-semibold">
                    {formatCurrency(unpaidInvoice.amount_due, unpaidInvoice.currency)}
                </span>
                {unpaidInvoice.due_date && <> due {formatDate(unpaidInvoice.due_date)}</>}
            </p>
            <button
                type="button"
                onClick={() => portalSession.mutate()}
                disabled={portalSession.isPending}
                className="secondary-button shrink-0 ml-3 !text-xs !px-3 !py-1.5 disabled:opacity-60"
            >
                Pay Now
            </button>
        </div>
    );
}

// ---------------------------------------------------------------------------
// TimeSavedMessage — "You've saved X hours writing records with HappyDoc!"
// ---------------------------------------------------------------------------

interface TimeSavedMessageProps {
    allTimeHappyVisits: number;
    className?: string;
    highlightClassName?: string;
}

export function TimeSavedMessage({
    allTimeHappyVisits,
    className = "text-sm text-gray-600",
    highlightClassName = "font-semibold text-gray-900",
}: TimeSavedMessageProps): React.JSX.Element | null {
    const timeSaved = formatTimeSaved(allTimeHappyVisits);
    if (!timeSaved) return null;

    return (
        <p className={className}>
            You've saved <span className={highlightClassName}>{timeSaved}</span> writing records with HappyDoc!
        </p>
    );
}

// ---------------------------------------------------------------------------
// BillingDetailLines — next invoice date, commitment renewal, etc.
// ---------------------------------------------------------------------------

interface BillingDetailLinesProps {
    lines: (string | null)[];
}

export function BillingDetailLines({ lines }: BillingDetailLinesProps): React.JSX.Element | null {
    const filteredLines = lines.filter(Boolean) as string[];
    if (filteredLines.length === 0) return null;

    return (
        <div className="flex flex-col gap-1 text-sm text-gray-600">
            {filteredLines.map((line) => (
                <p key={line}>{line}</p>
            ))}
        </div>
    );
}

// ---------------------------------------------------------------------------
// BillingIntervalToggle — Monthly/Annual toggle pill used in modals
// ---------------------------------------------------------------------------

interface BillingIntervalToggleProps {
    value: "month" | "year";
    onChange: (interval: "month" | "year") => void;
    annualDisabled?: boolean;
    /** Optional badge shown to the right of the toggle (e.g. "Save up to 20%") */
    trailingBadge?: React.ReactNode;
}

export function BillingIntervalToggle({
    value,
    onChange,
    annualDisabled = false,
    trailingBadge,
}: BillingIntervalToggleProps): React.JSX.Element {
    return (
        <div className="flex items-center justify-center">
            <div className="relative inline-flex items-center bg-gray-50 border border-gray-200 rounded-lg p-1">
                <button
                    type="button"
                    className={cn(
                        "px-3 py-2 rounded-md text-sm font-semibold transition-all",
                        value === "month" ? "bg-max-900 text-white shadow-sm" : "text-gray-500 hover:text-gray-700",
                    )}
                    onClick={() => onChange("month")}
                >
                    Monthly
                </button>
                <button
                    type="button"
                    className={cn(
                        "px-3 py-2 rounded-md text-sm font-semibold transition-all",
                        annualDisabled && "opacity-40 cursor-not-allowed",
                        value === "year" ? "bg-max-900 text-white shadow-sm" : "text-gray-500 hover:text-gray-700",
                    )}
                    onClick={() => onChange("year")}
                    disabled={annualDisabled}
                >
                    Annual
                </button>
                {trailingBadge}
            </div>
        </div>
    );
}
